import { DotLottie, DotLottieReact } from '@lottiefiles/dotlottie-react'
import { Box } from '@mui/material'
import { useEffect, useState } from 'react'
import { REWARDS_TABS, useNavTabs } from '../../../../components/navigation'
import { useStoreActions, useStoreState } from '../../../../store/hooks'

const fontStyles = {
    color: '#FFF',
    fill: '#FFF',
    textAlign: 'center',
    leadingTrim: 'both',
    textEdge: 'cap',
    fontFamily: 'Space Grotesk',
    fontSize: '42',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: '26px' /* 74.286% */,
    textTransform: 'uppercase',
    textShadow: '2px 4px 4px rgba(0, 0, 0, 1)',
    // textAnchor: 'middle',
}

export const RewardClaimed = () => {
    const [dotLottie, setDotLottie] = useState<DotLottie>()
    const [showReward, setShowReward] = useState(false)
    const claimedReward = useStoreState((state) => state.rewardsService.claimedReward)
    const setClaimedReward = useStoreActions((state) => state.rewardsService.setClaimedReward)
    const achievement = claimedReward?.achievements?.[0]
    const { changeTab } = useNavTabs()

    useEffect(() => {
        // This function will be called when the animation is completed.
        function onComplete() {
            setTimeout(() => {
                setClaimedReward(undefined)
                changeTab(REWARDS_TABS.ACHIEVEMENTS)
            }, 250)
        }

        function onFrameChange({ currentFrame }) {
            if (currentFrame > 4 && currentFrame < 85) {
                setShowReward(true)
            } else {
                setShowReward(false)
            }
        }

        // Listen to events emitted by the DotLottie instance when it is available.
        if (dotLottie) {
            dotLottie.addEventListener('complete', onComplete)
            dotLottie.addEventListener('frame', onFrameChange)
        }

        return () => {
            // Remove event listeners when the component is unmounted.
            if (dotLottie) {
                dotLottie.removeEventListener('complete', onComplete)
                dotLottie.removeEventListener('frame', onFrameChange)
            }
        }
    }, [dotLottie])

    const dotLottieRefCallback = (dotLottie) => {
        setDotLottie(dotLottie)
    }

    if (!achievement) return null

    const { assetUrl, counter } = achievement
    const isUpgraded = counter > 1

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    maxWidth: '600px',
                    width: '100%',
                    maxHeight: '600px',
                    height: '100%',
                    filter: 'drop-shadow(4px 10px 4px rgba(0, 0, 0, 0.75))',
                    position: 'relative',
                    'svg, #sparkles': {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                    },
                    svg: {
                        zIndex: 1,
                        '@keyframes earned': {
                            '0%': { opacity: 0, transform: 'scale(0)' },
                            '100%': { opacity: 1, transform: 'scale(1)' },
                        },
                        '@keyframes upgraded': {
                            '0%': { opacity: 0, transform: 'scale(3)' },
                            '100%': { opacity: 1, transform: 'scale(1)' },
                        },
                        animation: `${isUpgraded ? 'upgraded' : 'earned'} 1s ease-in-out forwards`,
                    },
                    '#sparkles': {
                        zIndex: 2,
                    },
                }}
            >
                <DotLottieReact
                    dotLottieRefCallback={dotLottieRefCallback}
                    src={`/assets/animations/rewards/achievement-${isUpgraded ? 'upgraded' : 'earned'}.lottie`}
                    autoplay
                    layout={{ fit: 'contain', align: [0, 0] }}
                    renderConfig={{
                        autoResize: true,
                    }}
                />
                {showReward && (
                    <>
                        <Box id="sparkles">
                            <DotLottieReact
                                src="/assets/animations/rewards/sparkles.lottie"
                                autoplay
                                layout={{ fit: 'contain', align: [0, 0] }}
                                renderConfig={{
                                    autoResize: true,
                                }}
                            />
                        </Box>
                        <svg
                            width="100%"
                            height="572px"
                            viewBox="0 0 557 572"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <image href={assetUrl} height="220" width="220" x={(557 - 220) / 2} y="190" />
                            {counter > 1 && (
                                <text
                                    {...fontStyles}
                                    x={557 / 2}
                                    y={330}
                                    fontSize="90"
                                    textAnchor="middle"
                                    stroke="black"
                                    strokeWidth="8"
                                    paintOrder="stroke"
                                >
                                    {counter}
                                </text>
                            )}
                        </svg>
                    </>
                )}
            </Box>
        </Box>
    )
}
